import { InAppDisplaySettingsDefinition, Position, MESSAGE_FORMAT } from 'modules/graphql/types';

export const SELECTION_OPTIONS = [
  {
    value: MESSAGE_FORMAT.Html,
    label: 'Content, styles, and layout (HTML)',
    id: MESSAGE_FORMAT.Html,
  },
  {
    value: MESSAGE_FORMAT.Json,
    label: 'Raw data (JSON)',
    id: MESSAGE_FORMAT.Json,
  },
  {
    value: MESSAGE_FORMAT.Everything,
    label: 'Everything: content, styles, layout, and raw data (HTML & JSON)',
    id: MESSAGE_FORMAT.Everything,
  },
];

export const SETTINGS_DISPLAY_CONTENT = {
  destinations: {
    app: { title: 'App', body: 'Available apps' },
    appAndBrowser: {
      title: 'App & Browser',
      body: 'Available apps & browsers',
    },
    roku: {
      title: 'Roku',
      body: 'Available apps',
    },
    button: 'Save in-app type',
    header: 'In-app content type',
    errors: {
      unset: 'In-app type required to create template',
    },
  },
  addRawButton: 'Add raw data',
  removeRawButton: 'Remove raw data',
  backgroundOverlay: {
    labelText: 'Background overlay',
    hexColorLabel: 'Hex color',
    hexColorPlaceholder: '000000',
    hexColorPrefix: '#',
    opacityHelpText: '0 (transparent) to 100 (opaque)',
    opacityLabel: 'Opacity',
    opacityPlaceholder: '80',
    opacityPrefix: '%',
    compatibilityLabel: 'Obscure the content behind the message. ',
  },
  deliverSilently: {
    checkboxLabel:
      'The user will not see your message until they tap it in the inbox. You can still draw attention to it in another way; with the unread count, for example.',
    labelText: 'Deliver silently',
  },
  expirationTime: {
    labelText: 'Expiration time',
    defaultRelativeTime: '+90d',
    relativeDateLabelText: 'Or set a relative date:',
    relativeDatePlaceholder: '+ relative time',
    relativeDatePrefix: 'send time',
    expirationTimeDefaultValue: 'send time +90d', // Confirmed with Jess and Nick that this is going to be always the default.
  },
  position: {
    labelText: 'Position',
  },
  presetAnimiations: {
    labelText: 'Preset animations',
    compatibilityLabel:
      "For full position, the message will fade in and out. If you're using CSS animations, disable this option ",
  },
  rawData: {
    headerLabel: 'Raw data (JSON)',
    buttonText: 'Add raw data',
  },
  showInInbox: {
    labelText: 'Show in mobile inbox',
  },
  mobileInboxTitle: {
    labelText: 'Mobile inbox title',
  },
  mobileInboxBody: {
    labelText: 'Mobile inbox body',
  },
  messageFormat: {
    labelText: 'Message format',
    helpText:
      'HTML messages are displayed exactly as defined in the editor (personalized for each contact, with Handlebars expressions being replaced with real data). Raw JSON data requires a custom message display, or it can be used to trigger custom functionality in your mobile or web app.',
  },
  mobileInboxThumbnail: {
    labelText: 'Mobile inbox thumbnail URL',
  },
  unsetText: 'Not set',
  handlebarsRestrictionText: 'Save and preview for accurate character count.',
  rawDataMessage: "Can't preview raw data",
  roku: {
    title: {
      label: 'Title',
      unset: 'Not set',
    },
    body: {
      label: 'Body',
      unset: 'Not set',
    },
    actionButtons: {
      label: 'Action buttons',
      unset: 'Not set',
      MAX_NUMBER_OF_BUTTONS: 2,
      deepLinkHelpText: 'A link to the content that should open when users click this button.',
      deepLinkDocs: 'https://support.iterable.com/hc/articles/12900805484692',
    },
    imageUrl: {
      label: 'Image URL',
      placeholder: 'https://placekitten.com/300/200',
      unset: 'Not set',
      error: 'Please provide a proper URL.',
      helpText:
        'Recommended aspect ratio is 16:9. Supports JPEG, PNG and (since Roku OS 9.4) WebP image files.',
    },
    backgroundColor: {
      label: 'Background color',
      defaultColor: '0x1C1C1C',
      unset: 'Not set',
    },
    fonts: {
      buttonLabel: 'Button font',
      bodyLabel: 'Body font',
      headerLabel: 'Title font',
      helpText:
        'Insert URI to the hosted .ttf font file in your Roku App. If no font URI is provided, Iterable will fall back to Source Sans Pro.',
      placeholder: 'fonts/font.ttf',
      unset: 'Not set',
      error: 'Font URI must include a .ttf file',
    },
  },
};

/** According to BE, values should be padding percentage 0-100, or None for autoexpand */
export const IN_APP_DISPLAY: Record<Position, InAppDisplaySettingsDefinition> = {
  [Position.Full]: {
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
  },
  [Position.Bottom]: {
    bottom: 0,
    left: 0,
    right: 0,
    top: null,
  },
  [Position.Top]: {
    left: 0,
    right: 0,
    top: 0,
    bottom: null,
  },
  [Position.Center]: {
    left: 0,
    right: 0,
    top: null,
    bottom: null,
  },
};

export enum IN_APP_VIEW_OPTIONS {
  inApp = 'inApp',
  inbox = 'inbox',
  web = 'web',
  roku = 'roku',
}

export enum PLATFORMS {
  ANDROID = 'android',
  IOS = 'iOS',
  RN = 'reactNative',
}

export const CONTENT = {
  hoverText: 'View SDK Compatibility',
  requires: 'This feature requires:',
  heading: {
    [PLATFORMS.ANDROID]: "Iterable's Android SDK:",
    [PLATFORMS.IOS]: "Iterable's iOS SDK:",
    [PLATFORMS.RN]: "Iterable's React Native SDK:",
  },
  version: 'version',
};

export const COMPATIBILITY = {
  android: '3.2.8+',
  iOS: '6.2.14+',
  reactNative: '1.0.18+',
};

export enum ANIMATION {
  slideIn = 'slide-in',
  slideOut = 'slide-out',
  fadeIn = 'fade-in',
  fadeOut = 'fade-out',
}

export const ANIMATION_DURATION = 400;
export const ANIMATION_DELAY = 300;
